import React, { useState } from 'react';
import { Flex } from 'antd';
import { DateTime } from 'luxon';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { MSTeamsUser, Study, StudyClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExInput } from 'jf/components/DevExInput';
import { DevExLink } from 'jf/components/DevExLink';
import { DevExText } from 'jf/components/DevExText';
import { MSTeamsTargetTags } from 'jf/pages/studyAnalyzer/participation/msteams/MSTeamsTargetTags';
import { SlackTargetTagList } from 'jf/pages/studyAnalyzer/participation/SlackTargetTagList';
import { useTargetingClient } from 'jf/services/useTargetingClient';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientMutation } from 'jf/utils/useClientQuery';

import { useStudyBuilderState } from '../StudyBuilderState';
import { useSyncMessagingUsersQuery } from '../targetRespondents/useSyncMessagingUsersQuery';
import { useStudyUpdater } from '../useStudyUpdater';

export const PublishStudyStep: React.FC = () => {
  const flags = useClientFlags();
  const navigate = useNavigate();
  const theme = useDevExTheme();
  const queryClient = useQueryClient();
  const state = useStudyBuilderState();
  const studyUpdater = useStudyUpdater(state.studyRef);
  const syncMessagingUsersQuery = useSyncMessagingUsersQuery();

  const [isPublishing, setIsPublishing] = useState(false);
  const [showTargets, setShowTargets] = useState(false);
  const [showRespondents, setShowRespondents] = useState(false);

  const { mutateAsync: updateStudy } = useClientMutation(StudyClient.updateStudy);
  const { useGetStudyTargets, msgProvider } = useTargetingClient();

  const { data: targets } = useGetStudyTargets({
    studyRef: state.studyRef,
    isEnabled: flags.slackTargeting,
  });

  const showMessengerTargeting = !!targets?.length;

  // Check if MS Teams sync is in progress. This helps prevent race conditions with target deletion during publishing
  // Only block if sync is still running and we have targets selected
  const isSyncInProgress =
    showMessengerTargeting &&
    (!syncMessagingUsersQuery.isFinished || syncMessagingUsersQuery.isFetching);

  const numRespondents = targets?.length || 0;

  const onBack = () => {
    state.update({ step: 'customize-study' });
  };

  const onPublish = () => {
    if (state.studyRef) {
      setIsPublishing(true);
      trackEvent('survey-editor:publish:publish', { surveyRef: state.studyRef });
      updateStudy({
        studyRef: state.studyRef,
        requestBody: {
          status: Study.status.OPEN,
        },
      })
        .then(() => {
          queryClient.invalidateQueries('GET_STUDIES');
          queryClient.invalidateQueries(['GET_STUDY', state.studyRef]);
          queryClient.invalidateQueries(['GET_STUDY_TARGETS', state.studyRef]);
          queryClient.invalidateQueries('GET_PROMPTS'); // in case prompts were frozen
          queryClient.removeQueries(['GET_PUBLIC_STUDY', state.studyRef]);
          navigate(`/study/${state.studyRef}/analyze/participation`);
        })
        .catch(() => {
          setIsPublishing(false);
        });
    }
  };

  const targetList = flags.msTeamsTargeting ? (
    <MSTeamsTargetTags msteamsTargets={targets as MSTeamsUser[]} />
  ) : (
    <SlackTargetTagList studyRef={state.studyRef!} />
  );

  if (flags.scheduledCloseReminders) {
    return (
      <Flex
        vertical
        style={{ fontSize: theme.variable.fontSize.md, lineHeight: theme.variable.lineHeight }}
        gap={theme.variable.spacing.lg}
      >
        <DevExText fontSize={'xl'} style={{ textAlign: 'center' }}>
          Review your survey
        </DevExText>
        <DevExCard title="Survey Name">
          <DevExText>
            Be sure to give your survey <DevExText strong>a standout name </DevExText>
            before you publish it!
          </DevExText>
          <DevExInput
            style={{ width: '100%' }}
            value={state.name}
            onChange={(value) => {
              state.update({ name: value });
              studyUpdater.update({ name: value });
            }}
          />
        </DevExCard>
        {showMessengerTargeting && (
          <DevExCard title="Survey Distribution">
            <DevExText lined>
              The survey will be sent individually to
              <DevExText strong>
                {' '}
                {numRespondents} {numRespondents === 1 ? 'person' : 'people'}{' '}
              </DevExText>
              in {msgProvider}.
            </DevExText>
            <DevExLink
              onClick={() => setShowRespondents(!showRespondents)}
              style={{ fontSize: theme.variable.fontSize.sm }}
            >
              {showRespondents ? 'Hide' : 'Show'} {numRespondents} respondent
              {numRespondents === 1 ? '' : 's'}
            </DevExLink>
            {showRespondents && targetList}
          </DevExCard>
        )}
        {showMessengerTargeting && (
          <DevExCard title="Survey Reminders">
            {state.enableScheduledReminders ? (
              <DevExText lined>
                Reminders will automatically be sent <DevExText strong> every 2 days </DevExText>
                via {msgProvider} to respondents who have not yet responded unless you disable them.
              </DevExText>
            ) : (
              <DevExText lined>
                No reminders will be automatically sent. You can still manually send reminders to
                respondents who have not yet responded. You can enable automatic reminders at any
                point while the survey is open.
              </DevExText>
            )}
          </DevExCard>
        )}
        {!!state.scheduledCloseDate &&
          (state.msteamsTargetingEnabled || state.slackTargetingEnabled) && (
            <DevExCard title="Survey Close Date">
              <DevExText lined>
                The survey will close automatically on{' '}
                <DevExText strong>
                  {DateTime.fromISO(state.scheduledCloseDate).toFormat('cccc, MMM d, yyyy')} at 9am
                  EST{' '}
                </DevExText>
                unless you close it before or you extend the deadline.
              </DevExText>
            </DevExCard>
          )}

        <DevExText type="secondary" lined>
          Once your survey is published, you will no longer be able to edit the questions. Are you
          ready to publish?
        </DevExText>

        <Flex justify="end" gap={theme.variable.spacing.sm}>
          <DevExButton
            type="primary"
            size="large"
            loading={isPublishing || state.updatingStudy || isSyncInProgress}
            disabled={state.updatingStudy || isSyncInProgress}
            onClick={onPublish}
          >
            {isSyncInProgress
              ? 'Verifying recipients...'
              : state.updatingStudy
                ? 'Saving'
                : isPublishing
                  ? 'Publishing'
                  : 'Yes, publish'}
          </DevExButton>
          <DevExButton size="large" disabled={isPublishing} onClick={onBack}>
            Not yet, take me back
          </DevExButton>
        </Flex>
      </Flex>
    );
  }
  return (
    <Flex
      vertical
      style={{ fontSize: theme.variable.fontSize.md, lineHeight: theme.variable.lineHeight }}
      gap={theme.variable.spacing.lg}
    >
      <div style={{ fontSize: theme.variable.fontSize.xl }}>Publish and Review</div>

      <Flex vertical gap={theme.variable.spacing.sm}>
        Be sure to give your study a standout name!
        <DevExInput
          value={state.name}
          onChange={(value) => {
            state.update({ name: value });
            studyUpdater.update({ name: value });
          }}
          placeholder="Add name"
          error={!state.name}
          size="large"
          style={{ borderRadius: theme.variable.borderRadius }}
        />
      </Flex>

      <Flex vertical gap={theme.variable.spacing.sm}>
        Once your survey is published, you will no longer be able to make changes to it.{' '}
        {targets?.length
          ? `The survey will be sent individually to ${targets.length} people in ${msgProvider}.`
          : 'You will get a link to share with recipients.'}
        {!!targets?.length && (
          <Flex vertical gap={theme.variable.spacing.sm}>
            <DevExLink
              onClick={() => setShowTargets(!showTargets)}
              style={{ fontSize: theme.variable.fontSize.sm, width: 'fit-content' }}
            >
              {showTargets ? 'Hide survey recipients' : 'Show survey recipients'}
            </DevExLink>
            {showTargets && targetList}
          </Flex>
        )}
      </Flex>

      <div>Are you ready to publish?</div>

      <Flex justify="start" gap={theme.variable.spacing.sm}>
        <DevExButton
          type="primary"
          size="large"
          loading={isPublishing || state.updatingStudy || isSyncInProgress}
          disabled={state.updatingStudy || isSyncInProgress}
          onClick={onPublish}
        >
          {isSyncInProgress
            ? 'Verifying recipients...'
            : state.updatingStudy
              ? 'Saving'
              : isPublishing
                ? 'Publishing'
                : 'Yes, publish'}
        </DevExButton>
        <DevExButton size="large" disabled={isPublishing} onClick={onBack}>
          Not yet, take me back
        </DevExButton>
      </Flex>
    </Flex>
  );
};
